import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  assignableRoles,
  type ResellerRoleKey,
  type ResellerRoleMatrix,
} from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";

import { ApiError } from "@/lib/api";

/**
 * Invite one person into a reseller company.
 *
 * The copy is deliberate about what this does, because it is not the same as
 * the Dashboard Users invite next door: it creates a CRM authorization binding
 * AND an external sign-in account on the reseller portal — not a staff account,
 * and not a dashboard login.
 */
export function InviteResellerMemberDialog({
  isOpen,
  resellerName,
  matrix,
  onClose,
  onSubmit,
  error,
  isSubmitting,
}: {
  isOpen: boolean;
  resellerName: string;
  /** The company's role matrix, so its custom roles can be picked too. */
  matrix?: ResellerRoleMatrix | undefined;
  onClose: () => void;
  onSubmit: (input: {
    email: string;
    role: ResellerRoleKey;
    name?: string;
  }) => Promise<void>;
  error: ApiError | null;
  isSubmitting: boolean;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<ResellerRoleKey>("salesperson");
  const roleOptions = assignableRoles(matrix);

  const reset = () => {
    setEmail("");
    setName("");
    setRole("salesperson");
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    const trimmedName = name.trim();
    try {
      await onSubmit({
        email: trimmed,
        role,
        ...(trimmedName ? { name: trimmedName } : {}),
      });
      reset();
      onClose();
    } catch {
      // The caller surfaces the ApiError through the `error` prop — keep the
      // dialog open so the operator can fix the address and retry.
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      PaperProps={{ sx: { maxWidth: 520 } }}
    >
      <DialogTitle>Invite a reseller user</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          Gives one person access to{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>{resellerName}</Box>&rsquo;s
          customers in the CRM, and emails them a link to set their own password
          on the reseller portal. This is an <strong>external</strong> account: it
          is not staff, it cannot sign in to this dashboard, and it only ever sees
          that reseller&rsquo;s own subtree.
        </DialogContentText>

        <Box
          component="form"
          id="invite-reseller-member-form"
          onSubmit={handleSubmit}
          sx={{ display: "grid", gap: 2, mt: 2 }}
        >
          <TextField
            autoFocus
            type="email"
            required
            size="small"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="rep@partner.example"
            inputProps={{ "data-cy": "reseller-member-email" }}
            // The email is the join key between the CRM binding and the portal
            // account, and it is not editable afterwards — say so here rather
            // than only in the error the operator gets later.
            helperText="Used to match their sign-in to this reseller. It can't be changed later — revoke and re-invite instead."
          />

          <TextField
            size="small"
            label="Full name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            helperText="Only used to greet them by name in the invitation email."
          />

          <TextField
            select
            size="small"
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value as ResellerRoleKey)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-cy": "reseller-member-role" }}
            helperText={`Owners can do everything. Salesperson, Technician and Viewer start from XOP's default permissions; custom roles hold exactly what is ticked for them under Roles at ${resellerName}. You can also adjust one person after inviting them.`}
          >
            {roleOptions.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </TextField>

          {error && (
            <Box
              role="alert"
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: (t) => alpha(t.palette.error.main, 0.4),
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                px: 1.5,
                py: 1,
              }}
            >
              <Typography sx={{ fontSize: 13, color: "error.main" }}>
                {error.message}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="invite-reseller-member-form"
          variant="contained"
          disabled={isSubmitting || email.trim().length === 0}
          data-cy="reseller-member-invite-submit"
        >
          {isSubmitting ? "Inviting…" : "Send invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
